'use client';

import { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

type WorkItem = {
  id: string;
  title: string;
  category: string;
  year: string;
  image: string;
  imageAlt: string;
  height: string;
};

const works: WorkItem[] = [
  {
    id: 'w1',
    title: 'Raag Yaman — Live at NCPA',
    category: 'Performance',
    year: '2024',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1418e4bfc-1772834277426.png',
    imageAlt: 'Live classical music performance on stage with dramatic lighting at NCPA Mumbai',
    height: 'h-[420px]',
  },
  {
    id: 'w2',
    title: 'Echoes of Silence — Book Launch',
    category: 'Writing',
    year: '2023',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_185c5e21a-1772834278776.png',
    imageAlt: 'Poetry book launch event with Kavita Thakur reading from her debut collection',
    height: 'h-[320px]',
  },
  {
    id: 'w3',
    title: 'TEDx Talk — Finding Your Voice',
    category: 'Coaching',
    year: '2021',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_19e44bd2d-1772253475276.png',
    imageAlt: 'TEDx stage presentation with Kavita Thakur speaking to a packed auditorium',
    height: 'h-[320px]',
  },
  {
    id: 'w4',
    title: 'Corporate Workshop Series',
    category: 'Coaching',
    year: '2024',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1716e36b5-1772834275781.png',
    imageAlt:
      'Group coaching workshop with professionals engaged in personality development exercises',
    height: 'h-[320px]',
  },
  {
    id: 'w5',
    title: 'Sitar Fusion Recital',
    category: 'Music',
    year: '2023',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1798971b3-1772834284121.png',
    imageAlt: 'Intimate sitar recital performance with warm candlelight ambiance',
    height: 'h-[320px]',
  },
];

const categoryColors: Record<string, string> = {
  Performance: '#B8966E',
  Writing: '#C9A99A',
  Coaching: '#8B9E8A',
  Music: '#B8966E',
};

export default function WorkSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [selectedWork, setSelectedWork] = useState<WorkItem | null>(null);
  const [isPortfolioOpen, setIsPortfolioOpen] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add('animated');
        });
      },
      { threshold: 0.1 }
    );

    const els = sectionRef.current?.querySelectorAll('.will-animate');
    els?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!selectedWork && !isPortfolioOpen) return;
    const onEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (selectedWork) {
        setSelectedWork(null);
        return;
      }
      setIsPortfolioOpen(false);
    };
    document.addEventListener('keydown', onEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onEscape);
      document.body.style.overflow = '';
    };
  }, [selectedWork, isPortfolioOpen]);

  return (
    <section
      id="work"
      ref={sectionRef}
      className="py-36 lg:py-44"
      style={{ background: '#F2E8DF' }}
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* HEADER */}
        <div className="grid lg:grid-cols-2 gap-12 items-end mb-20">
          <div className="scroll-reveal will-animate">
            <span className="section-label">Selected Work</span>

            <h2
              className="font-fraunces font-light leading-tight mt-3"
              style={{ fontSize: 'clamp(2.5rem,5vw,5rem)' }}
            >
              A life&apos;s <br />
              <em className="italic">highlights.</em>
            </h2>
          </div>

          <p className="text-muted text-base leading-relaxed max-w-md lg:ml-auto scroll-reveal will-animate">
            Performances, publications, and programs that have shaped Kavita&apos;s journey across
            two decades.
          </p>
        </div>

        {/* WORK GRID */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 scroll-reveal will-animate">
          {works.map((work, i) => (
            <div
              key={work.id}
              className={`group relative rounded-[28px] overflow-hidden cursor-pointer img-hover-scale ${work.height}`}
              style={{ transitionDelay: `${i * 0.08}s` }}
            >
              <AppImage
                src={work.image}
                alt={work.imageAlt}
                fill
                className="object-cover transition-transform duration-700 group-hover:scale-105"
                sizes="(max-width:768px) 100vw, 600px"
              />

              {/* Gradient overlay */}
              <div
                className="absolute inset-0"
                style={{
                  background: 'linear-gradient(to top, rgba(28,25,23,0.65) 0%, transparent 55%)',
                }}
              />

              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition duration-500" />

              {/* Content */}
              <div className="absolute inset-0 p-6 flex flex-col justify-between">
                {/* Top */}
                <div className="flex justify-between items-start">
                  <span
                    className="text-10 font-bold tracking-widest uppercase px-3 py-1 rounded-full"
                    style={{
                      background: 'rgba(250,246,240,0.18)',
                      backdropFilter: 'blur(8px)',
                      border: `1px solid ${categoryColors[work.category]}40`,
                      color: work.category === 'Coaching' ? '#B8D4B6' : '#E8D5C0',
                    }}
                  >
                    {work.category}
                  </span>

                  <span className="text-10 text-cream/70 tracking-wider">{work.year}</span>
                </div>

                {/* Bottom */}
                <div>
                  <h3 className="font-fraunces text-lg text-cream font-light leading-snug">
                    {work.title}
                  </h3>

                  <button
                    type="button"
                    onClick={() => setSelectedWork(work)}
                    className="mt-3 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0"
                  >
                    <span className="text-10 tracking-widest uppercase text-cream/70">View</span>
                    <Icon name="ArrowRightIcon" size={12} className="text-cream/70" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* BUTTON */}
        <div className="flex justify-center mt-16 scroll-reveal will-animate">
          <button type="button" className="btn-outline" onClick={() => setIsPortfolioOpen(true)}>
            View Full Portfolio
            <Icon name="ArrowRightIcon" size={14} />
          </button>
        </div>
      </div>

      {isPortfolioOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Full portfolio"
          onClick={() => setIsPortfolioOpen(false)}
        >
          <div
            className="w-full max-w-5xl rounded-[28px] overflow-hidden"
            style={{ background: '#F2E8DF', boxShadow: '0 30px 80px rgba(0,0,0,0.45)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-[rgba(184,150,110,0.2)]">
              <h3 className="font-fraunces text-2xl text-charcoal font-light">Full Portfolio</h3>
              <button
                type="button"
                onClick={() => setIsPortfolioOpen(false)}
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{
                  background: 'rgba(28,25,23,0.65)',
                  border: '1px solid rgba(250,246,240,0.25)',
                }}
                aria-label="Close full portfolio"
              >
                <Icon name="XMarkIcon" size={16} className="text-cream" />
              </button>
            </div>

            <div className="max-h-[70vh] overflow-y-auto p-6 sm:p-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {works.map((work) => (
                  <button
                    key={`portfolio-${work.id}`}
                    type="button"
                    onClick={() => {
                      setIsPortfolioOpen(false);
                      setSelectedWork(work);
                    }}
                    className="group rounded-2xl overflow-hidden border border-[rgba(184,150,110,0.2)] bg-[rgba(250,246,240,0.7)] text-left"
                  >
                    <div className="relative h-48">
                      <AppImage
                        src={work.image}
                        alt={work.imageAlt}
                        fill
                        className="object-cover"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <span className="text-10 tracking-widest uppercase text-muted">
                          {work.category}
                        </span>
                        <span className="text-10 text-muted">{work.year}</span>
                      </div>
                      <p className="mt-2 text-sm font-semibold text-charcoal">{work.title}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedWork && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          role="dialog"
          aria-modal="true"
          aria-label={`${selectedWork.title} details`}
          onClick={() => setSelectedWork(null)}
        >
          <div
            className="w-full max-w-3xl rounded-[28px] overflow-hidden"
            style={{ background: '#F2E8DF', boxShadow: '0 30px 80px rgba(0,0,0,0.45)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative w-full h-[260px] sm:h-[340px]">
              <AppImage
                src={selectedWork.image}
                alt={selectedWork.imageAlt}
                fill
                className="object-cover"
                sizes="(max-width: 768px) 100vw, 900px"
              />
              <button
                type="button"
                onClick={() => setSelectedWork(null)}
                className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center"
                style={{
                  background: 'rgba(28,25,23,0.65)',
                  border: '1px solid rgba(250,246,240,0.25)',
                }}
                aria-label="Close details"
              >
                <Icon name="XMarkIcon" size={16} className="text-cream" />
              </button>
            </div>
            <div className="p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-4">
                <span
                  className="text-10 font-bold tracking-widest uppercase px-3 py-1 rounded-full"
                  style={{
                    background: 'rgba(184,150,110,0.15)',
                    border: `1px solid ${categoryColors[selectedWork.category]}55`,
                    color: '#7A6E68',
                  }}
                >
                  {selectedWork.category}
                </span>
                <span className="text-xs text-muted">{selectedWork.year}</span>
              </div>
              <h3 className="font-fraunces text-3xl text-charcoal font-light leading-tight">
                {selectedWork.title}
              </h3>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
